import React, { useState } from 'react';
import Header from '../../components/Header/Header';
import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';
import Card from '../../components/Card/Card';
import { submitContactMessage } from '../../lib/contactApi';
import './Contact.css';

const Contact = ({ onGoHome, onNavigate }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await submitContactMessage({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
      });
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (err) {
      setSubmitError(err?.message || 'Could not send your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <Header onLogoClick={onGoHome} onNavigate={onNavigate} />
      <main className="contact-main">
        <div className="contact-container">
          {/* Hero Section */}
          <section className="contact-hero">
            <h1 className="contact-hero__title">Contact Us</h1>
            <p className="contact-hero__subtitle">
              We're here to help! Get in touch with our team for any questions or support.
            </p>
          </section>

          <div className="contact-content">
            {/* Contact Form */}
            <section className="contact-form-section">
              <Card variant="elevated" className="contact-form-card">
                <h2 className="contact-form__title">Send us a Message</h2>
                
                {isSubmitted ? (
                  <div className="contact-form__success">
                    <div className="contact-form__success-icon">✓</div>
                    <h3 className="contact-form__success-title">Message Sent!</h3>
                    <p className="contact-form__success-text">
                      Thank you for contacting us. We'll get back to you within 24 hours.
                    </p>
                    <Button 
                      variant="primary" 
                      size="md"
                      onClick={() => setIsSubmitted(false)}
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form className="contact-form" onSubmit={handleSubmit}>
                    {submitError && (
                      <p className="contact-form__submit-error" role="alert">
                        {submitError}
                      </p>
                    )}
                    <div className="contact-form__fields">
                      <div className="contact-form__field">
                        <Input
                          type="text"
                          label="Full Name"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          required
                          error={errors.name}
                        />
                      </div>

                      <div className="contact-form__field">
                        <Input
                          type="email"
                          label="Email Address"
                          placeholder="john.doe@example.com"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          required
                          error={errors.email}
                        />
                      </div>

                      <div className="contact-form__field">
                        <Input
                          type="tel"
                          label="Phone Number (Optional)"
                          placeholder="+1 (555) 123-4567"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          error={errors.phone}
                        />
                      </div>

                      <div className="contact-form__field">
                        <Input
                          type="text"
                          label="Subject"
                          placeholder="How can we help?"
                          value={formData.subject}
                          onChange={(e) => handleChange('subject', e.target.value)}
                          required
                          error={errors.subject}
                        />
                      </div>

                      <div className="contact-form__field">
                        <label className="input__label">
                          Message
                          <textarea
                            className="input"
                            placeholder="Tell us more about your inquiry..."
                            value={formData.message}
                            onChange={(e) => handleChange('message', e.target.value)}
                            rows="6"
                            required
                          />
                          {errors.message && (
                            <span className="input__error" role="alert">
                              {errors.message}
                            </span>
                          )}
                        </label>
                      </div>
                    </div>

                    <div className="contact-form__actions">
                      <Button
                        type="submit"
                        variant="cta"
                        size="lg"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </div>
                  </form>
                )}
              </Card>
            </section>

            {/* Contact Information */}
            <section className="contact-info-section">
              <Card variant="elevated" className="contact-info-card">
                <h2 className="contact-info__title">Get in Touch</h2>

                <div className="contact-info__items">
                  <div className="contact-info__item">
                    <div className="contact-info__icon">📞</div>
                    <div className="contact-info__content">
                      <h3 className="contact-info__label">Phone</h3>
                      <a href="tel:+201102866660" className="contact-info__value contact-info__value--phone">
                        +20 11 02866660
                      </a>
                    </div>
                  </div>
                </div>
              </Card>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Contact;
